import type { HeimdallSessionData } from '@highlight-cp/auth/heimdall-server';
import { z } from 'zod';

// One schema for the session: the server builds it with the types below, and
// the browser parses responses with it and keeps only what it lists. A field
// missing here fails the server build, not a page.
const impersonationProfileSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
});

// The Highlight employee behind the screen, on either employee-as-customer path
const impersonatorSchema = z.object({
  id: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
});

const baseAppSessionSchema = z.object({
  authSource: z.literal('heimdall'),
  email: z.string(),
  heimdallUserId: z.string(),
  org_id: z.number(),
  org_role: z.string(),
  team_ids: z.string(),
  user_id: z.string(),
});

// Discriminated unions, so a schema failure names the field that broke it.
const clientAppSessionSchema = z.discriminatedUnion('impersonation', [
  baseAppSessionSchema.extend({
    impersonation: z.literal(true),
    // The impersonated customer (who is being acted as)
    impersonation_target: impersonationProfileSchema,
    impersonator: impersonatorSchema.optional(),
    role: z.literal('client'),
  }),
  baseAppSessionSchema.extend({
    impersonation: z.literal(false).optional(),
    impersonation_target: z.undefined().optional(),
    impersonator: z.undefined().optional(),
    role: z.literal('client'),
  }),
]);

const adminAppSessionSchema = baseAppSessionSchema.extend({
  admin_scope_project_public_id: z.string(),
  impersonation: z.undefined().optional(),
  impersonation_target: z.undefined().optional(),
  // The employee themself: admin scope acts as a customer without ever swapping session identity.
  impersonator: impersonatorSchema.optional(),
  role: z.literal('admin'),
});

export const clientPortalSessionSchema = z.discriminatedUnion('role', [
  clientAppSessionSchema,
  adminAppSessionSchema,
]);

export type ClientPortalImpersonationProfile = z.infer<typeof impersonationProfileSchema>;
export type ClientPortalImpersonator = z.infer<typeof impersonatorSchema>;
export type ClientPortalClientAppSession = z.infer<typeof clientAppSessionSchema>;
export type ClientPortalAdminAppSession = z.infer<typeof adminAppSessionSchema>;
export type ClientPortalAppSession = z.infer<typeof clientPortalSessionSchema>;

export interface ProjectTeamReference {
  id?: number | null | undefined;
  teamId?: number | null | undefined;
}

export interface ProjectCustomer {
  id?: string | number | null;
  firstName?: string | null;
  lastName?: string | null;
  emailAddress?: string | null;
  teams?: Array<ProjectTeamReference | null> | null;
  organization?: {
    id?: number | null | undefined;
    teams?: Array<ProjectTeamReference | null> | null | undefined;
    isDeleted?: boolean | undefined;
  } | null;
  organizationRole?: string | null;
}

export interface ProjectCustomerSessionFields {
  customerId: string | number;
  customerEmail: string;
  organizationId: number;
  organizationRole: string;
  teamIds: number[];
}

export class ClientPortalAppSessionError extends Error {
  constructor(public code: 'invalid-session' | 'invalid-customer' | 'invalid-impersonation') {
    super(code);
    this.name = 'ClientPortalAppSessionError';
  }
}

const requireNonEmptyString = (value: unknown): string | null => {
  return typeof value === 'string' && value.trim() ? value : null;
};

export const getProjectTeamId = (team: ProjectTeamReference | null): number | null => {
  if (typeof team?.id === 'number') {
    return team.id;
  }

  return typeof team?.teamId === 'number' ? team.teamId : null;
};

const isNumber = (value: unknown): value is number => typeof value === 'number';

export const toProjectCustomerSessionFields = (
  customer: ProjectCustomer | null | undefined,
  {
    requireTeamIds,
    teams = customer?.teams,
  }: { requireTeamIds: boolean; teams?: ProjectCustomer['teams'] },
): ProjectCustomerSessionFields | null => {
  const customerId = customer?.id;
  const customerEmail = requireNonEmptyString(customer?.emailAddress)?.toLowerCase();
  const organizationId = customer?.organization?.id;
  const organizationRole = requireNonEmptyString(customer?.organizationRole);
  const teamIds = teams?.map(getProjectTeamId);

  if (
    (typeof customerId !== 'string' && typeof customerId !== 'number') ||
    !customerEmail ||
    typeof organizationId !== 'number' ||
    !organizationRole ||
    !teamIds ||
    (requireTeamIds && teamIds.length === 0) ||
    !teamIds.every(isNumber)
  ) {
    return null;
  }

  return {
    customerId,
    customerEmail,
    organizationId,
    organizationRole,
    teamIds,
  };
};

const toCustomerImpersonationProfile = (
  customer: ProjectCustomer | null | undefined,
): ClientPortalImpersonationProfile | null => {
  const firstName = requireNonEmptyString(customer?.firstName);
  const lastName = requireNonEmptyString(customer?.lastName);
  const email = requireNonEmptyString(customer?.emailAddress);

  if (!firstName || !lastName || !email) {
    return null;
  }

  return { firstName, lastName, email: email.toLowerCase() };
};

export function getNormalizedClientPortalEmail(session: HeimdallSessionData): string | null {
  const email = requireNonEmptyString(session.user?.email);

  return email?.toLowerCase() ?? null;
}

/**
 * Heimdall stores a single display name (Google profile); map it to `firstName`
 * so the top nav renders it as the "Actual User".
 */
function toEmployeeImpersonator(
  id: string,
  employee: { name?: string | null; email?: string | null } | null | undefined,
): ClientPortalImpersonator {
  return {
    id,
    firstName: requireNonEmptyString(employee?.name) ?? undefined,
    email: requireNonEmptyString(employee?.email)?.toLowerCase(),
  };
}

export function toClientPortalSession({
  heimdallSession,
  customer,
}: {
  heimdallSession: HeimdallSessionData;
  customer: ProjectCustomer | null | undefined;
}): ClientPortalClientAppSession {
  const heimdallUserId = requireNonEmptyString(heimdallSession.user?.id);
  const heimdallEmail = getNormalizedClientPortalEmail(heimdallSession);

  if (!heimdallUserId || !heimdallEmail) {
    throw new ClientPortalAppSessionError('invalid-session');
  }

  const betterAuthImpersonatedBy = requireNonEmptyString(heimdallSession.session?.impersonatedBy);
  const betterAuthImpersonationTarget = betterAuthImpersonatedBy
    ? toCustomerImpersonationProfile(customer)
    : null;

  if (betterAuthImpersonatedBy && !betterAuthImpersonationTarget) {
    throw new ClientPortalAppSessionError('invalid-impersonation');
  }

  const customerFields = toProjectCustomerSessionFields(customer, { requireTeamIds: false });
  if (!customerFields || customerFields.customerEmail !== heimdallEmail) {
    throw new ClientPortalAppSessionError('invalid-customer');
  }

  const appSession = {
    email: customerFields.customerEmail,
    heimdallUserId,
    role: 'client',
    user_id: String(customerFields.customerId),
    team_ids: `{${customerFields.teamIds.join(',')}}`,
    org_id: customerFields.organizationId,
    org_role: customerFields.organizationRole,
    authSource: 'heimdall',
  } satisfies ClientPortalClientAppSession;

  if (!betterAuthImpersonatedBy || !betterAuthImpersonationTarget) {
    return appSession;
  }

  return {
    ...appSession,
    impersonation: true,
    impersonation_target: betterAuthImpersonationTarget,
    impersonator: toEmployeeImpersonator(betterAuthImpersonatedBy, heimdallSession.impersonator),
  };
}

export function toAdminClientPortalSession({
  heimdallSession,
  customer,
  projectPublicId,
}: {
  heimdallSession: HeimdallSessionData;
  customer: ProjectCustomer | null | undefined;
  projectPublicId: string;
}): ClientPortalAdminAppSession {
  const adminHeimdallUserId = requireNonEmptyString(heimdallSession.user?.id);
  const adminScopeProjectPublicId = requireNonEmptyString(projectPublicId);

  if (!adminHeimdallUserId || !adminScopeProjectPublicId) {
    throw new ClientPortalAppSessionError('invalid-session');
  }

  const adminTeams = customer?.organization?.teams?.length
    ? customer.organization.teams
    : customer?.teams;
  const customerFields = toProjectCustomerSessionFields(customer, {
    requireTeamIds: true,
    teams: adminTeams,
  });
  if (!customerFields) {
    throw new ClientPortalAppSessionError('invalid-customer');
  }

  return {
    email: customerFields.customerEmail,
    heimdallUserId: adminHeimdallUserId,
    role: 'admin',
    user_id: String(customerFields.customerId),
    team_ids: `{${customerFields.teamIds.join(',')}}`,
    org_id: customerFields.organizationId,
    org_role: customerFields.organizationRole,
    authSource: 'heimdall',
    admin_scope_project_public_id: adminScopeProjectPublicId,
    impersonator: toEmployeeImpersonator(adminHeimdallUserId, heimdallSession.user),
  };
}
